
import React from 'react';
import { Theme } from '../types';

interface RulerProps {
  orientation: 'horizontal' | 'vertical';
  length: number; // in meters
  pixelScale: number;
  size: number; // thickness of the ruler in pixels
  theme: Theme;
}

const Ruler: React.FC<RulerProps> = ({ orientation, length, pixelScale, size, theme }) => {
  const totalPxLength = length * pixelScale;
  const numMajorTicks = Math.floor(length / 5); // Major tick every 5 meters
  const numMinorTicks = Math.floor(length);    // Minor tick every 1 meter

  const rulerBgColor = theme === 'dark' ? 'bg-hba-mid-purple' : 'bg-gray-300';
  const textColor = theme === 'dark' ? 'text-hba-text-secondary' : 'text-gray-500';
  const tickColor = theme === 'dark' ? 'bg-hba-text-secondary' : 'bg-gray-500';

  const ticks = [];

  // Minor ticks
  for (let i = 0; i <= numMinorTicks; i++) {
    if (i * pixelScale > totalPxLength + pixelScale/2) break; // Prevent ticks going too far past end
    const pos = i * pixelScale;
    ticks.push(
      <div
        key={`minor-${i}`}
        className={`absolute ${tickColor}`}
        style={
          orientation === 'horizontal'
            ? { left: `${pos}px`, bottom: 0, width: '1px', height: `${size / 3}px` }
            : { top: `${pos}px`, right: 0, height: '1px', width: `${size / 3}px` }
        }
        data-ruler-tick="minor" 
      />
    );
  }

  // Major ticks and labels
  for (let i = 0; i <= numMajorTicks; i++) {
    const meterValue = i * 5;
    if (meterValue * pixelScale > totalPxLength + pixelScale / 2) break;
    const pos = meterValue * pixelScale;
    ticks.push(
      <div
        key={`major-${i}`}
        className={`absolute ${tickColor}`}
        style={
          orientation === 'horizontal'
            ? { left: `${pos}px`, bottom: 0, width: '1px', height: `${size / 2}px` }
            : { top: `${pos}px`, right: 0, height: '1px', width: `${size / 2}px` }
        }
        data-ruler-tick="major"
      />
    );
    ticks.push(
      <span
        key={`label-${i}`}
        className={`absolute ${textColor} text-xs`}
        style={
          orientation === 'horizontal'
            ? { left: `${pos + 2}px`, bottom: `${size / 2 - 6}px` } 
            : { top: `${pos + 2}px`, right: `${size / 2 - 2}px`, transform: 'translateX(50%)' }
        }
        data-ruler-label="true"
      >
        {meterValue}m
      </span>
    );
  }
   // Final tick at the very end of the venue if not covered by a major/minor tick already
   if (totalPxLength > 0 && Math.abs(length - Math.round(length)) > 0.001 && Math.round(length * pixelScale) > (numMinorTicks * pixelScale)) {
    ticks.push(
        <div
            key="end-tick"
            className={`absolute ${tickColor}`}
            style={
            orientation === 'horizontal'
                ? { left: `${totalPxLength}px`, bottom: 0, width: '1px', height: `${size / 3}px` }
                : { top: `${totalPxLength}px`, right: 0, height: '1px', width: `${size / 3}px` }
            }
            data-ruler-tick="end"
        />
    );
   }


  return (
    <div
      data-type="ruler" 
      className={`absolute ${rulerBgColor} select-none`}
      style={
        orientation === 'horizontal'
          ? { top: 0, left: `${size}px`, width: `${totalPxLength}px`, height: `${size}px` }
          : { top: `${size}px`, left: 0, height: `${totalPxLength}px`, width: `${size}px` }
      }
    >
      {ticks}
    </div>
  );
};

export default Ruler;
